import { format, formatDistanceToNow, isToday, isYesterday, isThisWeek, getYear, differenceInMinutes } from "date-fns";
import { useTranslation } from "react-i18next";
import { ar, enUS } from "date-fns/locale";

export const messagesFormatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();

  if (isToday(date)) {
    const timeAgo = formatDistanceToNow(date, { addSuffix: true });
    return timeAgo === "less than a minute ago" ? "Just now" : timeAgo;
  } else if (isYesterday(date)) {
    return `Yesterday at ${format(date, "hh:mm a")}`;
  } else if (isThisWeek(date)) {
    return `${format(date, "EEEE")}`;
  } else if (getYear(date) === getYear(now) - 1) {
    // Handles messages from last year
    return `Last year at ${format(date, "do MMMM")}`;
  } else {
    return format(date, "do MMMM yyyy");
  }
};

/**
 * Checks if a user is online based on the last seen timestamp and a threshold.
 *
 * @param lastSeenTimestamp - The timestamp of the user's last activity.
 * @param thresholdInMinutes - The threshold in minutes to consider the user online. Default is 5 minutes.
 * @returns `true` if the user is online, `false` otherwise.
 */
export const isUserOnline = (lastSeenTimestamp: number | null | undefined, thresholdInMinutes: number = 5): boolean => {
  if (!lastSeenTimestamp) {
    return false;
  }

  const lastSeenDate = new Date(lastSeenTimestamp);
  const currentDate = new Date();

  return differenceInMinutes(currentDate, lastSeenDate) <= thresholdInMinutes;
};

export const useFormatLocalizedDate = (isoString: string) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === "ar" ? ar : enUS;

  return format(new Date(isoString), "dd MMM, yyyy", { locale });
};
